import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { PageStyleService } from '@reconcept-platform/shared/util';

export type SectionType = 'default' | 'plain' | 'on-bg-base';

@Component({
  selector: 'rcp-section',
  template: `
    <div class="rcp-section" [class]="computedType()">
      @if (imageUrl()) {
        <div class="section-image__wrapper">
          <img [src]="imageUrl()" class="section-image" />
          @if (label() || description()) {
            <div class="section-image__header">
              @if (label()) {
                <div class="section-image__label">
                  {{ label() }}
                </div>
              }
              @if (description()) {
                <div class="section-image__description">
                  {{ description() }}
                </div>
              }
            </div>
          }
        </div>
      } @else if (label() || description()) {
        <div class="rcp-section-header">
          <div class="rcp-section-header-title-container">
            @if (showTitleBar()) {
              <div class="rcp-section-header-title">
                @if (collapsible()) {
                  <fa-icon
                    class="collapsable-icon clickable"
                    [icon]="faChevronDown"
                    [rotate]="collapsed() ? 270 : undefined"
                    (click)="collapseSection()"
                  />
                }
                @if (label()) {
                  <div class="rcp-section-header-detail">
                    <ng-content select="[leading-detail]" />
                  </div>
                  <span
                    class="rcp-section-header-title-label ellipsis"
                    (click)="collapseSection()"
                    [class.clickable]="collapsible()"
                    >{{ label() }}</span
                  >
                  <div class="rcp-section-header-detail">
                    <ng-content select="[trailing-detail]" />
                  </div>
                }
              </div>
            }

            @if (description() && expanded()) {
              <span class="rcp-section-description">{{ description() }}</span>
            }

            @if (expanded()) {
              <ng-content select="[description-content]" />
            }
          </div>

          <div class="rcp-section-header-actions">
            <ng-content select="[actions]" />
          </div>
        </div>
      }

      @if (expanded()) {
        <div class="rcp-section-filters">
          <ng-content select="[filter-bar]" />
        </div>

        <div class="rcp-section-body">
          <ng-content />
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      /* This needs to be set to block for now, as we don't want the section to take up full height of it's
         container when by default. */
      display: block;
      --negative-section-padding: calc(-1 * var(--section-padding));
    }

    .section-image {
      height: 100%;
      width: 100%;
      object-fit: cover;
      border-radius: var(--border-radius-md) var(--border-radius-md) 0 0;
    }

    .section-image__wrapper {
      position: relative;
      height: var(--image-section-fold-height);
      margin: var(--negative-section-padding) var(--negative-section-padding) 0 var(--negative-section-padding);
      border-radius: var(--border-radius-md) var(--border-radius-md) 0 0;
    }

    .section-image__header {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      padding: var(--section-padding);
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 90%);
      color: var(--col-text-guaranteed-on-dark);
      display: flex;
      flex-direction: column;
      gap: var(--item-internal-spacing-md);
    }

    .section-image__label {
      font-size: var(--font-size-header-2);
    }
    .section-image__description {
      font-size: var(--font-size-normal);
    }

    .rcp-section {
      .rcp-section-header-detail,
      .rcp-section-header-actions,
      .rcp-section-body,
      .rcp-section-filters {
        &:empty {
          display: none;
        }
      }
    }

    .rcp-section {
      display: flex;
      flex-direction: column;
      padding: var(--section-padding);
      gap: var(--section-padding);
      background-color: var(--col-bg-base);

      &.default {
        border-radius: var(--border-radius-md);
        box-shadow: var(--effect-shadow-md);
      }

      &.plain {
        padding: 0;
        box-shadow: 0;
        background-color: transparent;
      }

      &.on-bg-base {
        box-shadow: 0;
        background-color: var(--col-bg-base);
        border: 1px solid var(--col-lines);
        border-radius: var(--border-radius-md);
      }

      .rcp-section-header {
        display: flex;
        justify-content: space-between;

        .rcp-section-header-title-container {
          display: flex;
          flex-direction: column;
          gap: var(--text-header-spacing);
          color: var(--col-text-headers);
          min-width: 0;

          .rcp-section-header-title {
            display: flex;
            gap: var(--item-spacing-between-md);
            align-items: center;

            .collapsable-icon {
              margin-right: 0;
            }
          }

          .rcp-section-header-title-label {
            /* unset to use the font set on the body */
            font-size: var(--section-label-font-size, unset);
            font-weight: var(--section-label-font-weight, var(--font-weight-semi-bold));
          }
        }

        .rcp-section-header-actions {
          flex-shrink: 0;
          display: flex;
          /* 
            start puts actions at top 
            actions is next to title + description, which can grow. 'center' would mess that up
           */
          align-items: start;
        }
      }

      .rcp-section-filters {
        display: flex;
        justify-content: space-between;
      }

      .rcp-section-description {
        white-space: pre-line;
      }

      .rcp-section-body {
        display: flex;
        flex-direction: column;
        gap: var(--section-padding);
      }
    }
  `,
  imports: [FaIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SectionComponent {
  private readonly pageStyleService = inject(PageStyleService);

  readonly type = input<SectionType>();

  readonly collapsible = input<boolean>();
  readonly startCollapsed = input<boolean>(true);

  readonly label = input<string>();
  readonly description = input<string>();

  readonly imageUrl = input<string>();

  readonly collapsed = linkedSignal<boolean>(() => this.startCollapsed());
  readonly expanded = computed(() => !this.collapsed() || !this.collapsible());

  // Use typeInput as style if set,
  // else if rcp section is used inside a standalone page, use a default value of 'plain'
  // else use 'default'
  readonly computedType = computed<SectionType>(
    () => this.type() || (this.pageStyleService.pageStyle() === 'standalone' ? 'plain' : 'default'),
  );

  readonly showTitleBar = computed(() => this.collapsible() || !!this.label());

  collapseSection() {
    if (this.collapsible()) {
      this.collapsed.update((collapsed) => !collapsed);
    }
  }

  protected readonly faChevronDown = faChevronDown;
}
